/**
 * StrategyCodeViewer — read-only syntax-highlighted view of a user strategy's
 * TypeScript source code. Replaces the right panel when codeViewOpen is true.
 */

import { useEffect, useRef } from 'react';
import Prism from 'prismjs';
import 'prismjs/components/prism-typescript';
import { useConfigurationStore } from '../../stores/configurationStore.js';
import { useUserStrategy } from '../../hooks/useUserStrategies.js';
import { Spinner } from '../Spinner/Spinner.js';

// ============================================================================
// Prism dark theme (VS Code Dark+ palette)
// ============================================================================

const PRISM_THEME = `
  .token.comment,
  .token.prolog,
  .token.doctype,
  .token.cdata {
    color: #6a9955;
    font-style: italic;
  }
  .token.string,
  .token.template-string,
  .token.attr-value {
    color: #ce9178;
  }
  .token.keyword,
  .token.control,
  .token.directive,
  .token.unit,
  .token.rule {
    color: #569cd6;
  }
  .token.boolean {
    color: #569cd6;
  }
  .token.number {
    color: #b5cea8;
  }
  .token.operator {
    color: #d4d4d4;
  }
  .token.function {
    color: #dcdcaa;
  }
  .token.class-name {
    color: #4ec9b0;
  }
  .token.builtin {
    color: #4ec9b0;
  }
  .token.punctuation {
    color: #d4d4d4;
  }
  .token.variable {
    color: #9cdcfe;
  }
  .token.property {
    color: #9cdcfe;
  }
  .token.parameter {
    color: #9cdcfe;
  }
  .token.type-declaration .token.class-name {
    color: #4ec9b0;
  }
`;

// ============================================================================
// Component
// ============================================================================

export function StrategyCodeViewer() {
  const { selectedStrategyId, setCodeViewOpen, codeViewVersionCode } = useConfigurationStore();
  const { data: strategy, isLoading } = useUserStrategy(selectedStrategyId);

  const codeRef = useRef<HTMLElement>(null);

  // Inject Prism theme styles into <head> for the lifetime of this component.
  useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-prism-theme', '');
    style.textContent = PRISM_THEME;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Use the pinned version's code when set (historical version selected),
  // otherwise fall back to the current strategy's code.
  const codeToDisplay = codeViewVersionCode ?? strategy?.code ?? '';

  const highlightedCode = codeToDisplay
    ? Prism.highlight(codeToDisplay, Prism.languages.typescript, 'typescript')
    : '';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: '#111827',
      }}
    >
      {/* ── Back button header ─────────────────────────────────────────────── */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid #333',
          flexShrink: 0,
        }}
      >
        <button
          onClick={() => setCodeViewOpen(false)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 14,
            color: '#93c5fd',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to {strategy?.name ?? 'Strategy'}
        </button>
      </div>

      {/* ── Code block area ────────────────────────────────────────────────── */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px 20px',
        }}
      >
        {isLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: '#6b7280',
            }}
          >
            <Spinner size="lg" />
          </div>
        ) : (
          <>
            <p
              style={{
                margin: 0,
                marginBottom: 12,
                fontSize: 13,
                color: '#666',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Strategy Code
            </p>

            <pre
              style={{
                margin: 0,
                padding: 16,
                background: '#0d1117',
                borderRadius: 8,
                border: '1px solid #2a2a2a',
                overflow: 'auto',
                fontSize: 13,
                lineHeight: 1.6,
                fontFamily:
                  "'JetBrains Mono', 'Fira Code', 'SF Mono', 'Consolas', 'Menlo', monospace",
              }}
            >
              <code
                ref={codeRef}
                className="language-typescript"
                dangerouslySetInnerHTML={{ __html: highlightedCode }}
              />
            </pre>
          </>
        )}
      </div>
    </div>
  );
}
